//import packages
import React, { useState, useEffect, useMemo } from "react";
import { Route, Redirect, Switch, useHistory, useLocation } from "react-router-dom";
import { Layout } from "antd";

//import components
import SocketContext from "../context/socketContext";
import Sidebar from "../layout/Sidebar";
import MainDrawer from "../layout/MainDrawer";
import MainHeader from "../layout/MainHeader";
import MainMenu from "../layout/MainMenu";
import MainDictionaryComponent from "./Dictionary";
import PhysicianComponent from "./Physicians";
import { ImpersonateProvider } from './Physicians/ImpersonateProvider';
import ManageCases from "./Cases/ManageCases";
import ReportComponent from "./Reports";
import CaseTicketComponent from "./CaseTickets";
import PhysicianCaseQueue from "./PhysicianCaseQueue"
import { getTickets } from "../api/CaseTicketsAPI";
import {
  StyledInnerLayout,
  StyledOuterLayout,
} from "../components/LayoutComponent";
import ProfileView from "../components/ProfileComponent/ProfileView";
import ReportChartComponent from "./ReportCharts";
import { CaseView } from '@veritas/case-view';

// Import API classes from admin panel
import * as CasesAPI from "../api/CasesAPI";
import * as PhysiciansAPI from "../api/PhysiciansAPI";
import * as PrescriptionsAPI from "../api/PrescriptionsApi";
import * as PatientAPI from "../api/PatientAPI";
import * as ChatRoomAPI from "../api/ChatRoomAPi";
import * as TicketAPI from "../api/CaseTicketsAPI";
import * as TemplateAPI from "../api/TemplateAPI";
import * as CaseTagsAPI from "../api/CaseTagsAPI";
import * as PharmacyAPI from "../api/PharmacyAPI";
import * as LabsAPI from "../api/LabsApi";
import * as LookupAPI from "../api/LookUpAPI";
import * as CaseTicketsAPI from "../api/CaseTicketsAPI";
import * as LookUpAPI from "../api/LookUpAPI";
import * as ConsultationAPI from "../api/ConsultationAPI";

import { useTicketByCaseId } from "../hooks/useTicketByCaseId";
import { useCaseDetails } from "../hooks/useCaseDetails";

import SchedulesComponent from "./Schedules";
import UserRolesAndRightsHOComponent from "../components/UserRolesAndRightsHOComponent/UserRolesAndRightsHOComponent";
import UnauthorizedComponent from "../components/UnauthorizedComponent/UnauthorizedComponent";
import authorization from "../auth/authorization";
import Articles from "./Articles";
import ArticleView from "./Articles/ArticleView";
import { AppointmentCalendar } from './AppointmentCalendar';
import DataGenerator from './DataGenerator';
import socket, { socket_init, socket_close } from "../api/socket";
import { PhysicianQueueSocketProvider } from "../context/PhysicianQueueSocketContext";
import { PhysicianReportSocketProvider } from "../context/PhysicianReportSocketContext";
import { CustomReportSocketProvider } from "../context/CustomReportSocketContext";
import { useAppConfig } from "../hooks/useAppConfig";

import { UserRoleEnum } from "../types/enums";

const apiClasses = {
  PhysicianAPI: PhysiciansAPI,
  CaseRegisterApi: CasesAPI,
  CasesApi: CasesAPI,
  PatientApi: PatientAPI,
  PrescriptionsAPI: PrescriptionsAPI,
  ChatRoomAPI: ChatRoomAPI,
  TicketAPI: TicketAPI,
  TemplatesAPI: TemplateAPI,
  CaseTagsAPI: CaseTagsAPI,
  CaseTicketsAPI: CaseTicketsAPI,
  PharmacyAPI: PharmacyAPI,
  LabsAPI: LabsAPI,
  AuthorizationAPI: authorization,
  LookUpAPI,
  ConsultationAPI,
};

const ContentComponent = ({ userDetails, userType }) => {
  const history = useHistory();
  const location = useLocation();
  const [showSidebar, setSidebarDisplay] = useState(false);
  const [collapsedSidebar, setSidebarCollapseStatus] = useState(false);
  const [showSidebarDrawer, setSidebarDrawerStatus] = useState(false);
  const [unreadTicketsCount, setUnreadTicketsCount] = useState();
  const ENABLE_DATA_GENERATOR = useAppConfig('ENABLE_DATA_GENERATOR');

  const canDeleteDocuments = authorization.hasPolicies(['deleteAdditionalDocuments']);

  const handleNavigateToCase = useMemo(() => (caseId, requestId, apiType) => {
    const path = `/cases/${caseId}?requestId=${requestId || 'initial'}`;
    history.push(path);
  }, [history]);

  const handleTicketClosed = useMemo(() => () => {
    if (location.state?.fromCaseTickets) {
      history.push('/case-tickets');
    }
  }, [history, location]);

  const changeSidebarStatus = (status) => {
    setSidebarDisplay(status);
    setSidebarCollapseStatus(status);
  };

  const changeSidebarDrawerStatus = (status) => {
    setSidebarDrawerStatus((previousDrawerStatus) => {
      return !previousDrawerStatus;
    });
  };

  const getCaseTicketNotificationCount = () => {
    getTickets({ status: 'open', count: true  })
      .then((res) => {
        const { data } = res;
        if (data && data.success) {
          const { payload } = data;
          if (payload) {
            setUnreadTicketsCount(payload.unread);
          }
        } else {
          console.log("Unable to get Case Tickets Notification Count");
        }
      })
      .catch((error) => {
        console.log(
          "Error occured while getting Case Ticket Notification Count"
        );
      });
  };

  useEffect(() => {
    if (authorization.hasAccessToModule(userType, ["caseTicketListing"])) {
      getCaseTicketNotificationCount();
      socket_init();

      socket.on("newTicketChatMessageRecieved", (data) => {
        console.log( "========= ContentComponent newTicketChatMessageRecieved event data ==========", data);
        const { users } = data;
        if (users?.includes(authorization.getUserId())) {
          getCaseTicketNotificationCount();
        }
      });

      //TODO: clearTicketNotificationSend is deprecated
      socket.on("clearTicketNotificationSend", (data) => {
        console.log(
          "============================== clearNotification event data ==============================="
        );

        if (authorization.getUserId() === data.userId) {
          getCaseTicketNotificationCount();
        }
      });

      socket.on("markedTicketAsRead", (data) => {
        console.log("============= markedTicketAsRead event data ==============", data);
        if (authorization.getUserId() === data.userId) {
          getCaseTicketNotificationCount();
        }
      });

      socket.on("error", (err) => {
        console.log(
          "============================== socket error =====================================",
          err
        );
      });

      return () => {
        socket.removeAllListeners([
          "newTicketChatMessageRecieved",
          "clearTicketNotificationSend",
          "markedTicketAsRead",
        ]);
        socket_close();
      };
    } // eslint-disable-next-line
  }, []);

  return (
    <>
      <SocketContext.Provider value={socket}>
        <CustomReportSocketProvider>
        <PhysicianReportSocketProvider>
        <PhysicianQueueSocketProvider>
        <StyledOuterLayout>
          <MainHeader
            userDetails={userDetails}
            changeSidebarCollapseState={changeSidebarDrawerStatus}
          />
          <Layout>
            {showSidebar && (
              <MainDrawer
                sidebarDrawerStatus={showSidebarDrawer}
                changeSidebarDrawerState={changeSidebarDrawerStatus}
              >
                <MainMenu notificationCount={unreadTicketsCount} />
              </MainDrawer>
            )}
            <Sidebar
              collapsed={collapsedSidebar}
              changeCollapseState={changeSidebarStatus}
            >
              <MainMenu notificationCount={unreadTicketsCount} />
            </Sidebar>
            <StyledInnerLayout>
              <Switch>
              <Route
                path="/dictionary"
                render={() => {
                  return <MainDictionaryComponent />
                }}
              />
              <Route
                exact
                path="/physicians"
                render={() => {
                  return (
                    <>
                      {authorization.hasAccessToModule(userType, [
                        "getPhysicianForAdmin",
                        "physicianModerationByAdmin",
                        "registerPhysician",
                        "getPhysicianDetailsForAdmin",
                        "updatePhysicianDetails",
                        "updatePhysicianSchedule",
                      ]) ? (
                        <PhysicianComponent />
                      ) : (
                        <UnauthorizedComponent />
                      )}
                    </>
                  );
                }}
              />
              <Route
                exact
                path='/providers/:providerId/impersonate'
                render={() => {
                  return (
                    <>
                      <ImpersonateProvider />
                    </>
                  )
                }}
              />
              <Route
                exact
                path="/cases"
                render={() => {
                  return (
                    <>
                      {authorization.hasAccessToModule(userType, [
                        "getCases",
                        "getCasesDetails",
                        "updateCase",
                        "caseStatusModification",
                      ]) ? (
                        <ManageCases
                          userDetails={userDetails}
                        />
                      ) : (
                        <UnauthorizedComponent />
                      )}
                    </>
                  );
                }}
              />
              <Route
                exact
                path="/report-charts"
                render={() => {
                  return (
                    <>
                      {authorization.hasAccessToModule(userType, [
                        "consultCompleteReport",
                        "consultNoRXReport",
                        "clientCasesReport",
                        "physicianCaseReport",
                      ]) ? (
                        <ReportChartComponent />
                      ) : (
                        <UnauthorizedComponent />
                      )}
                    </>
                  );
                }}
              />
              <Route
                exact
                path="/report"
                render={() => {
                  return (
                    <>
                      {authorization.hasAccessToModule(userType, [
                        "consultCompleteReport",
                        "consultNoRXReport",
                        "clientCasesReport",
                        "physicianCaseReport",
                      ]) ? (
                        <ReportComponent />
                      ) : (
                        <UnauthorizedComponent />
                      )}
                    </>
                  );
                }}
              />
              <Route
                exact
                path="/case-tickets"
                render={() => {
                  return (
                    <>
                      {authorization.hasAccessToModule(userType, [
                        "caseTicketListing",
                      ]) ? (
                        <CaseTicketComponent />
                      ) : (
                        <UnauthorizedComponent />
                      )}
                    </>
                  );
                }}
              />
              <Route
                path="/cases/:id"
                render={({ match, location }) => {
                  return (
                    <>
                      {authorization.hasAccessToModule(userType, [
                        "getCases",
                        "getCasesDetails",
                      ]) ? (
                        <CaseView
                          physicianDetails={userDetails}
                          useTicketByCaseId={useTicketByCaseId}
                          useCaseDetails={useCaseDetails}
                          apiType="admin"
                          caseId={match.params.id}
                          requestId={new URLSearchParams(location.search).get('requestId')}
                          apiClasses={apiClasses}
                          readOnlyMode={true}
                          allowTagEditing={true}
                          canDeleteDocuments={canDeleteDocuments}
                          onNavigateToCase={handleNavigateToCase}
                          onTicketClosed={handleTicketClosed}
                        />
                      ) : (
                        <UnauthorizedComponent />
                      )}
                    </>
                  );
                }}
              />
              <Route
                exact
                path="/schedule-availability/:id"
                render={() => {
                  return (
                    <>
                      {authorization.hasAccessToModule(userType, [
                        "updatePhysicianSchedule",
                        "getPhysicianAvailability",
                      ]) ? (
                        <SchedulesComponent />
                      ) : (
                        <UnauthorizedComponent />
                      )}
                    </>
                  );
                }}
              />
              <Route exact path="/my-profile" component={ProfileView} />
              <Route
                path="/"
                exact
                render={() => {
                  switch(userType) {
                    case UserRoleEnum.IT_SYSTEM_ADMIN:
                      return <Redirect to="/physicians" />
                    default:
                      return <Redirect to="/report-charts" />
                  }
                }}
              />
              <Route
                exact
                path="/articles"
                render={() => {
                  return <Articles userType={userType}/>;
                }}
              />
              <Route
                path="/articles/:id"
                render={() => {
                  return <ArticleView />;
                }}
              />
              <Route
                path='/appointments'
                render={(props) => <AppointmentCalendar {...props} />}
              />
              <Route
                path='/physician-case-queue/:physicianId'
                render={(props) => <PhysicianCaseQueue {...props} />}
              />
              {(ENABLE_DATA_GENERATOR && (
                <Route
                  exact
                  path='/data-generator'
                  render={() => <DataGenerator />}
                />
              ))}
              <Route
                path='*'
                render={(props) => <Redirect to="/report-charts" />}
              />
          </Switch>    
        </StyledInnerLayout>
          </Layout>
        </StyledOuterLayout>
        </PhysicianQueueSocketProvider>
        </PhysicianReportSocketProvider>
        </CustomReportSocketProvider>
      </SocketContext.Provider>
    </>
  );
};

export default UserRolesAndRightsHOComponent(ContentComponent);
